import { useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";

const MAGENTA_BEAM = "#FF4DB8";
const SPARK_BLUE = "#4E9CFF";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/confirm?next=/update-password`,
    });

    if (resetError) {
      setError(resetError.message || "Failed to send reset link. Please try again.");
    } else {
      setSent(true);
    }
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12"
      style={{
        background: `linear-gradient(135deg, ${MAGENTA_BEAM}08, white 50%, ${SPARK_BLUE}08)`,
      }}
    >
      <div className="flex justify-center mb-8">
        <HelpGenieLogo size={56} />
      </div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-md p-8 md:p-10">
        {sent ? (
          <div className="text-center">
            <div className="mb-4 mx-auto w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
              <Mail className="h-6 w-6 text-green-500" />
            </div>
            <h2
              className="text-2xl md:text-3xl font-bold mb-3"
              style={{
                background: `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Check Your Email
            </h2>
            <p className="text-gray-500 mb-6">
              We've sent a password reset link to <CopyableEmail email={email} className="font-medium text-gray-700" />. Check your inbox and follow the link to reset your password.
            </p>
            <Link to="/signin">
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Sign In
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="mb-4 mx-auto w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${MAGENTA_BEAM}15, ${SPARK_BLUE}15)` }}>
                <Mail className="h-6 w-6 text-genie-primary" />
              </div>
              <h2
                className="text-2xl md:text-3xl font-bold mb-2"
                style={{
                  background: `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Forgot Your Password?
              </h2>
              <p className="text-gray-500 text-sm">
                No worries! Enter your email and we'll send you a reset link.
              </p>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="email-address" className="text-sm font-medium text-gray-700">Email Address</Label>
                <Input
                  id="email-address"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 text-center p-3 bg-red-50 rounded-lg border border-red-100">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-genie-primary hover:bg-genie-primary/90 text-white font-medium rounded-lg"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>

            <div className="text-center mt-4">
              <Link to="/signin" className="text-sm text-genie-primary hover:text-genie-primary/80 transition-colors inline-flex items-center gap-1">
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Sign In
              </Link>
            </div>
          </>
        )}
      </div>

      <p className="mt-6 text-xs text-gray-400">
        &copy; {new Date().getFullYear()} Help Genie. All rights reserved.
      </p>
    </div>
  );
}
